import { supabase } from '@/utils/supabase';
import { validateAllocationInvariants, round4 } from '@/utils/concessionWaterfall';
import { readEdgeFunctionErrorBody } from '@/services/edgeFunctionErrorBody';

export interface LendwiseConcession {
  id: string;
  loan_id: string;
  amount: number;
  amount_points: number;
  reason: string;
  created_by: string;
  creator_name: string;
  created_at: string;
  is_legacy: boolean;
  loan_amount_snapshot: number | null;
  total_lo_price_before: number | null;
  total_lo_price_after: number | null;
  level1_absorbed: number;
  branch_management_absorbed: number;
  margin_absorbed: number;
}

interface RawConcessionRow {
  id: string;
  loan_id: string;
  amount: number;
  amount_points: number;
  reason: string;
  created_by: string;
  created_at: string;
  is_legacy: boolean;
  loan_amount_snapshot: number | null;
  total_lo_price_before: number | null;
  total_lo_price_after: number | null;
  level1_absorbed: number;
  branch_management_absorbed: number;
  margin_absorbed: number;
  users: { first_name: string | null; last_name: string | null; email: string | null } | null;
}

function normalizeRow(row: RawConcessionRow): LendwiseConcession {
  const u = row.users;
  const firstName = u?.first_name?.trim() || '';
  const lastName = u?.last_name?.trim() || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const email = u?.email;
  const creatorName = fullName || (email && email.length > 0 ? email : null) || 'Unknown';

  return {
    id: row.id,
    loan_id: row.loan_id,
    amount: Number(row.amount),
    amount_points: Number(row.amount_points),
    reason: row.reason,
    created_by: row.created_by,
    creator_name: creatorName,
    created_at: row.created_at,
    is_legacy: row.is_legacy,
    loan_amount_snapshot: row.loan_amount_snapshot !== null ? Number(row.loan_amount_snapshot) : null,
    total_lo_price_before: row.total_lo_price_before !== null ? Number(row.total_lo_price_before) : null,
    total_lo_price_after: row.total_lo_price_after !== null ? Number(row.total_lo_price_after) : null,
    level1_absorbed: Number(row.level1_absorbed),
    branch_management_absorbed: Number(row.branch_management_absorbed),
    margin_absorbed: Number(row.margin_absorbed)
  };
}

const SELECT_COLUMNS = 'id, loan_id, amount, amount_points, reason, created_by, created_at, is_legacy, loan_amount_snapshot, total_lo_price_before, total_lo_price_after, level1_absorbed, branch_management_absorbed, margin_absorbed, users:created_by(first_name, last_name, email)';

async function fetchConcessionsRaw(loanId: string): Promise<LendwiseConcession[]> {
  const { data, error } = await supabase
    .from('lendwise_concessions')
    .select(SELECT_COLUMNS)
    .eq('loan_id', loanId)
    .order('created_at', { ascending: false });

  if (error) {
throw error;
}
  // Fail closed on anomalous payloads: `[]` means "no concessions"; a non-array
  // response is an UNKNOWN read and must never be coerced into zero concessions
  // (Banker Recon blocks saves on concession-read failure).
  if (!Array.isArray(data)) {
    throw new Error('Concession read returned a non-array payload');
  }
  return (data as unknown as RawConcessionRow[]).map(normalizeRow);
}

export async function fetchConcessions(loanId: string): Promise<LendwiseConcession[]> {
  return fetchConcessionsRaw(loanId);
}

/** Fail closed: an ACTIVE concession with a non-finite value is unknown truth, not zero. */
function requireFiniteConcessionValue(value: number, field: string, concessionId: string): number {
  if (!Number.isFinite(value)) {
    throw new Error(`Active concession ${concessionId} has a non-finite ${field}`);
  }
  return value;
}

/** Σ active (non-legacy) concession points — the pricing-waterfall input. */
export function sumActiveConcessionPoints(concessions: LendwiseConcession[]): number {
  return concessions
    .filter(c => !c.is_legacy)
    .reduce((sum, c) => sum + requireFiniteConcessionValue(Number(c.amount_points), 'amount_points', c.id), 0);
}

/** Σ active (non-legacy) entered concession dollars — audit context, not pricing math. */
export function sumActiveConcessionDollars(concessions: LendwiseConcession[]): number {
  return concessions
    .filter(c => !c.is_legacy)
    .reduce((sum, c) => sum + requireFiniteConcessionValue(Number(c.amount), 'amount', c.id), 0);
}

export interface AddConcessionParams {
  loanId: string;
  amount: number;
  amountPoints: number;
  reason: string;
  loanAmountSnapshot: number;
  totalLoPriceBefore: number;
  totalLoPriceAfter: number;
  level1Absorbed: number;
  branchManagementAbsorbed: number;
  marginAbsorbed: number;
}

export async function addConcession(params: AddConcessionParams): Promise<LendwiseConcession[]> {
  const {
    loanId, amount, amountPoints, reason,
    loanAmountSnapshot, totalLoPriceBefore, totalLoPriceAfter,
    level1Absorbed, branchManagementAbsorbed, marginAbsorbed
  } = params;

  if (amount <= 0) {
throw new Error('Amount must be greater than 0');
}
  if (amountPoints <= 0) {
throw new Error('Amount points must be greater than 0');
}
  if (!reason.trim()) {
throw new Error('Reason is required');
}

  // Shared invariant check: nonneg + exact sum (round4 normalized)
  const invariantCheck = validateAllocationInvariants(
    level1Absorbed, branchManagementAbsorbed, marginAbsorbed, amountPoints
  );
  if (!invariantCheck.valid) {
throw new Error(invariantCheck.error);
}

  // Persist the normalized allocation from the validator, not raw inputs
  const { allocation } = invariantCheck;

  if (allocation.margin_absorbed > 0) {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError) {
throw authError;
}
    const email = (authData.user?.email ?? '').toLowerCase();
    if (email !== 'anthony@lendwisemtg.com') {
      throw new Error('Concessions cannot absorb Lendwise Margin');
    }
  }

  const { error } = await supabase
    .from('lendwise_concessions')
    .insert({
      loan_id: loanId,
      amount,
      amount_points: round4(amountPoints),
      reason: reason.trim(),
      is_legacy: false,
      loan_amount_snapshot: loanAmountSnapshot,
      total_lo_price_before: totalLoPriceBefore,
      total_lo_price_after: totalLoPriceAfter,
      level1_absorbed: allocation.level1_absorbed,
      branch_management_absorbed: allocation.branch_management_absorbed,
      margin_absorbed: allocation.margin_absorbed
    });

  if (error) {
throw error;
}
  return fetchConcessionsRaw(loanId);
}

export async function deleteConcession(
  id: string,
  loanId: string
): Promise<LendwiseConcession[]> {
  const { data, error } = await supabase
    .from('lendwise_concessions')
    .delete()
    .eq('id', id)
    .eq('loan_id', loanId)
    .select('id');

  if (error) {
throw error;
}
  if (!data || data.length === 0) {
    throw new Error('Concession was not deleted. Refresh and try again, or confirm you have permission to delete this concession.');
  }
  return fetchConcessionsRaw(loanId);
}

// ---------------------------------------------------------------------------
// Par-guarded removal (flag `pricing_set_active_par_guard` on)
// ---------------------------------------------------------------------------

export const CONCESSION_DELETE_ERROR_MESSAGES: Record<string, string> = {
  BELOW_PAR: 'Removing this concession puts the loan below par',
  ENCOMPASS_READ_FAILED: "Couldn't read the loan's pricing from Encompass — try again.",
  FEATURE_DISABLED: 'Set Active guard is off — refresh the page.',
  BANKER_RECON_FINALIZED: 'Reopen Banker Recon before making changes.',
  CONCESSION_DELETE_DENIED: 'Only the person who added this concession, or an admin, can remove it.',
  CONCESSION_NOT_FOUND: 'This concession is no longer on the loan. Refresh and try again.',
  PRICING_CHANGED: 'Pricing changed under you — the list is refreshed, try again.',
  ACTIVE_GROUP_IN_FLIGHT: 'Finish the pending pricing save first',
  LOAN_ACCESS_DENIED: "You don't have access to this loan.",
  NETWORK_ERROR: 'Could not reach the server. Try again.',
  CONCESSION_DELETE_FAILED: 'Failed to delete concession'
};

export class ConcessionDeleteError extends Error {
  readonly code: string;
  readonly shortfallPoints?: number;
  readonly shortfallDollars?: number;

  constructor(code: string, detail?: { shortfallPoints?: number; shortfallDollars?: number }) {
    super(CONCESSION_DELETE_ERROR_MESSAGES[code] ?? CONCESSION_DELETE_ERROR_MESSAGES.CONCESSION_DELETE_FAILED);
    this.name = 'ConcessionDeleteError';
    this.code = code;
    this.shortfallPoints = detail?.shortfallPoints;
    this.shortfallDollars = detail?.shortfallDollars;
  }
}

/**
 * Remove a concession through the server, which first projects par on the
 * active lock's economics, the live Encompass pricing and the remaining
 * concessions (`enqueue-encompass-write` `delete-concession`). The delete runs
 * with the caller's JWT, so the usual RLS and audit apply. Flag-off callers keep
 * `deleteConcession` above.
 */
export async function deleteConcessionGuarded(args: {
  loanId: string;
  encompassLoanId: string;
  concessionId: string;
}): Promise<LendwiseConcession[]> {
  let ok = false;
  try {
    const result = await supabase.functions.invoke<{ ok: boolean }>('enqueue-encompass-write', {
      body: {
        action: 'delete-concession',
        loanId: args.loanId,
        encompassLoanId: args.encompassLoanId,
        concessionId: args.concessionId
      }
    });
    if (result.error) {
      const parsed = await readEdgeFunctionErrorBody(result.error, result.response);
      const code = typeof parsed?.error === 'string' ? parsed.error : 'CONCESSION_DELETE_FAILED';
      throw new ConcessionDeleteError(code, {
        shortfallPoints: typeof parsed?.shortfallPoints === 'number' ? parsed.shortfallPoints : undefined,
        shortfallDollars: typeof parsed?.shortfallDollars === 'number' ? parsed.shortfallDollars : undefined
      });
    }
    ok = result.data?.ok === true;
  } catch (e) {
    if (e instanceof ConcessionDeleteError) {
      throw e;
    }
    throw new ConcessionDeleteError('NETWORK_ERROR');
  }
  if (!ok) {
    throw new ConcessionDeleteError('CONCESSION_DELETE_FAILED');
  }
  return fetchConcessionsRaw(args.loanId);
}
